import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import { mdiPlus } from '@mdi/js';
import { useApolloClient, useMutation } from '@apollo/client/react';

import {
    CreateOfferEventEnrollmentsDocument,
    IOfferEventDetailFragment,
    IOfferEventType,
} from 'graphql/types';
import { ActionButtonDropdown } from 'common/components/ActionButtonDropdown';
import { FilePicker } from 'common/components/FilePicker';
import { Icon } from 'common/components/Icon';
import { useApolloError } from 'common/hooks/useApolloError';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { IGlobalDrawerType } from 'common/types';
import { parseJSON } from 'common/utils/json';
import { UserImportBlockedDialog } from 'user/components/UserImportBlockedDialog';
import { UserImportModal } from 'user/components/UserImportModal';
import { useUserImportGuard } from 'user/hooks/useUserImportGuard';
import { useAppPermissions } from 'user/hooks/usePermissions';
import { useUserImport } from 'user/hooks/useUserImport';
import { EUsersImportType } from 'user/types';

import { EUserSelectorMode } from './types';

interface IAddUsersHandlers {
    onAddUsers: (userIds: string[], groupId?: string) => Promise<void>;
    loadingAddUsers: boolean;
    isUserSelectorOpen: boolean;
    userSelectorMode: EUserSelectorMode;
    onUserSelectorOpenChange: (open: boolean) => void;
}

interface IProps {
    offerEvent: IOfferEventDetailFragment;
    includeProgress: boolean;
    onEnrollmentsCreated: () => void;
    children: (handlers: IAddUsersHandlers) => ReactNode;
}

export const AddUsersButton = ({
    offerEvent,
    includeProgress,
    onEnrollmentsCreated,
    children,
}: IProps) => {
    const [translate] = useTranslation();
    const client = useApolloClient();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const { openGlobalDrawer } = useGlobalDrawer();
    const [isUserSelectorOpen, setIsUserSelectorOpen] = useState(false);
    const [userSelectorMode, setUserSelectorMode] = useState(
        EUserSelectorMode.Add
    );

    const {
        completeImportData,
        importModalOpen,
        handleFileUpload,
        handleImportModalClose,
    } = useUserImport({
        onUploadError: (_fileName, message) => {
            displaySnackbar(message, { variant: 'error' });
        },
    });

    const { canCreateUser = false } = useAppPermissions(
        'general',
        'management'
    );

    const { id: offerEventId, permissions } = offerEvent;
    const permissionsObj = parseJSON(permissions || '');
    const { canImportUsers = false } = permissionsObj || {};

    const {
        guardedImportClick,
        checkingImports,
        importBlockedDialogOpen,
        handleImportBlockedClose,
    } = useUserImportGuard({ canImport: canImportUsers });

    const [createEnrollments, { loading: loadingAddUsers }] = useMutation(
        CreateOfferEventEnrollmentsDocument
    );

    const evictEnrollmentsFromCache = useCallback(() => {
        client.cache.evict({
            fieldName: 'manageableOfferEventEnrollments',
        });
        client.cache.evict({
            fieldName: 'manageableOfferEventEnrollmentsForEvent',
        });
        client.cache.evict({
            fieldName: 'paginatedOfferEvents',
        });
        client.cache.gc();
    }, [client]);

    const handleAddUsers = useCallback(
        async (userIds: string[], groupId?: string) => {
            try {
                await createEnrollments({
                    variables: {
                        offerEventId,
                        users: userIds,
                        includeProgress,
                        groupId: groupId || undefined,
                    },
                });
            } catch (error) {
                showApolloError(error);

                return;
            }

            evictEnrollmentsFromCache();

            client.cache.evict({ fieldName: 'paginatedUsers' });
            client.cache.gc();

            displaySnackbar(
                translate('trainingParticipants.participantsAddedSuccess', {
                    count: userIds.length,
                }),
                { variant: 'success' }
            );

            onEnrollmentsCreated();
            setIsUserSelectorOpen(false);
        },
        [
            createEnrollments,
            offerEventId,
            includeProgress,
            showApolloError,
            evictEnrollmentsFromCache,
            client,
            displaySnackbar,
            translate,
            onEnrollmentsCreated,
        ]
    );

    const handleOpenCreateDrawer = useCallback(() => {
        openGlobalDrawer({
            type: IGlobalDrawerType.UserCreate,
            props: {
                onCreate: (userId: string) => {
                    handleAddUsers([userId]);
                },
            },
        });
    }, [openGlobalDrawer, handleAddUsers]);

    const handleImportFile = useCallback(() => {
        guardedImportClick('offer-event-users-import-csv');
    }, [guardedImportClick]);

    const handleImportSuccess = useCallback(
        (_succeededCount: number) => {
            client.cache.evict({ fieldName: 'paginatedUsers' });
            client.cache.gc();

            evictEnrollmentsFromCache();
            onEnrollmentsCreated();
        },
        [client, evictEnrollmentsFromCache, onEnrollmentsCreated]
    );

    const options = [
        {
            key: 'add-existing',
            label: translate('userSelector.addExistingUsers'),
            onClick: () => {
                setUserSelectorMode(EUserSelectorMode.Add);
                setIsUserSelectorOpen(true);
            },
        },
        {
            key: 'email-users',
            label: translate('userSelector.emailUsersAboutOffer'),
            onClick: () => {
                setUserSelectorMode(EUserSelectorMode.Email);
                setIsUserSelectorOpen(true);
            },
        },
        ...(canCreateUser
            ? [
                  {
                      key: 'create-new',
                      label: translate('userSelector.createUser'),
                      onClick: handleOpenCreateDrawer,
                  },
              ]
            : []),
        ...(canImportUsers
            ? [
                  {
                      key: 'import-file',
                      label: translate('userSelector.importFile'),
                      onClick: handleImportFile,
                  },
              ]
            : []),
    ];

    const [primaryOption] = options;

    const menuItems = options.map((option) => (
        <MenuItem key={option.key} onClick={option.onClick}>
            {option.label}
        </MenuItem>
    ));

    return (
        <>
            <ActionButtonDropdown
                disabled={checkingImports}
                menuItems={options.length > 1 ? menuItems : undefined}
                size="medium"
                sx={{ mb: 3 }}
                onClick={primaryOption.onClick}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {checkingImports ? (
                        <CircularProgress color="inherit" size={20} />
                    ) : (
                        <Icon path={mdiPlus} />
                    )}
                </Box>

                {primaryOption.label}
            </ActionButtonDropdown>

            <UserImportBlockedDialog
                open={importBlockedDialogOpen}
                onClose={handleImportBlockedClose}
            />

            {canImportUsers && (
                <>
                    <FilePicker
                        id="offer-event-users-import-csv"
                        inputProps={{ accept: '.csv' }}
                        sx={{
                            position: 'absolute',
                            overflow: 'hidden',
                            width: 0,
                            height: 0,
                        }}
                        onUpload={handleFileUpload}
                    />

                    <UserImportModal
                        forRelatedIds={[offerEventId]}
                        importData={completeImportData}
                        importType={EUsersImportType.OfferEvent}
                        open={importModalOpen}
                        showTrainingEmail={
                            offerEvent.type === IOfferEventType.Training
                        }
                        onClose={handleImportModalClose}
                        onSuccess={handleImportSuccess}
                    />
                </>
            )}

            {children({
                onAddUsers: handleAddUsers,
                loadingAddUsers,
                isUserSelectorOpen,
                userSelectorMode,
                onUserSelectorOpenChange: setIsUserSelectorOpen,
            })}
        </>
    );
};
